import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  fetchStudioBrandKit,
  fetchStudioFeatures,
  fetchStudioMusic,
  fetchStudioOutputSize,
  fetchStudioVideoJob,
  renderStudioVideo,
  uploadStudioAsset,
  uploadStudioVoiceover,
} from "../../../features/studio-controller/StudioServices";
import type { MediaItem } from "../../../types/studioTypes";
import { ensureFonts, loadFonts } from "../fonts";
import { MediaPicker } from "../MediaPicker";
import { Icon, type IconName } from "../Icon";
import { drawLayer, layerPicture, layersToPngs } from "../videoText";
import { Modal } from "../editor/Controls";
import {
  animState,
  audioGainAt,
  audioMaxDur,
  audioSourceTime,
  BLANK_COLORS,
  clamp,
  filterCss,
  fmt,
  frameAt,
  GalleryItem,
  layout,
  newAudioItem,
  newClip,
  r1,
  solidColorDataUrl,
  sourceTime,
  splitAt,
  splitAudio,
  transitionStyles,
  uid,
} from "./timeline";
import { VideoTimeline } from "./VideoTimeline";
import { VideoPanels, VIDEO_RAIL } from "./videoPanels";
import type {
  AspectKey,
  Box,
  Clip,
  MediaKind,
  OutputSize,
  ProbeResult,
  ProbeSource,
  Quality,
  Recording,
  RenderJob,
  RenderSpec,
  Selection,
  StudioFeatures,
  VideoDoc,
  VideoEditorApi,
  VideoPanelKey,
  BrandKit,
  StudioMusicTrack,
  TimelineLayout,
  MediaEl,
  SideStyle,
  TextLayer,
  VideoLayer,
  AudioItem,
  AudioRole,
  LayerPatch,
  VideoStart,
} from "../../../types/videoType";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";

const HISTORY_LIMIT = 60;
const DEFAULT_FEATURES: StudioFeatures = {
  maxVideoSeconds: 180,
  maxHdVideoSeconds: 300,
  maxClips: 20,
  maxAudios: 16,
};
const EMPTY_BRAND: BrandKit = { colors: [], logos: [] };

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(" ");

const BTN =
  "inline-flex min-h-10 items-center justify-center gap-2 whitespace-nowrap rounded-10 border border-st-line px-2 lg:px-3.5 py-[9px] text-sm font-semibold leading-none text-st-ink hover:border-[#9fb3ab] disabled:cursor-not-allowed disabled:opacity-50";
const BTN_ACCENT =
  "border-st-green bg-st-green-dark text-white hover:border-st-green-dark ";
const ICON_BTN_DARK =
  "grid h-[38px] w-[38px] flex-none place-items-center rounded-10 border-0 bg-transparent text-[#c4d6cf] hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-35";
const BANNER =
  "flex items-center justify-between gap-3 rounded-10 bg-[#fbe9e5] py-2.5 pl-3.5 pr-2.5 text-sm text-[#8f2c1d]";
const HINT = "mt-2 text-[13px] leading-normal text-st-muted";
const ERROR_TEXT = "mt-2 text-[13px] text-st-red";

interface VideoEditorProps {
  onClose: () => void;
  /** Media and music to start with (from the design editor's Elements). */
  start?: VideoStart;
}

interface HistoryState {
  stack: string[];
  index: number;
  timer: ReturnType<typeof setTimeout> | undefined;
}

const emptyDoc = (): VideoDoc => ({
  clips: [],
  layers: [],
  aspect: "PORTRAIT_9_16",
  fit: "FIT",
  background: "BLUR",
  quality: "WHATSAPP",
  audios: [],
  duck: true,
  name: "My video",
});

/** Reads a Gallery item's duration and size. */
export function probe(item: ProbeSource): Promise<ProbeResult | null> {
  return new Promise((resolve) => {
    if (item.mediaType === "IMAGE") {
      const img = new Image();
      img.onload = () =>
        resolve({
          duration: 0,
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      img.onerror = () => resolve({ duration: 0, width: 1080, height: 1080 });
      img.src = item.mediaUrl;
      return;
    }
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.onloadedmetadata = () =>
      resolve({
        duration: v.duration,
        width: v.videoWidth,
        height: v.videoHeight,
      });
    v.onerror = () => resolve(null);
    v.src = item.mediaUrl;
  });
}

function errorMessage(e: unknown, fallback: string): string {
  if (typeof e === "string") return e;
  if (e instanceof Error) return e.message;
  return fallback;
}

export default function VideoEditor({ onClose, start }: VideoEditorProps) {
  const dispatch = useDispatch<AppDispatch>();

  const [doc, setDocState] = useState<VideoDoc>(emptyDoc);
  const docRef = useRef<VideoDoc>(doc);
  const hist = useRef<HistoryState>({ stack: [], index: -1, timer: undefined });
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const [t, setT] = useState(0);
  const tRef = useRef(0);
  const [playing, setPlaying] = useState(false);
  const playRef = useRef<{ perf: number; from: number } | null>(null);
  const [sel, setSel] = useState<Selection>(null);
  const selRef = useRef<Selection>(sel);
  selRef.current = sel;
  const [panel, setPanel] = useState<VideoPanelKey>("media");
  const [features, setFeatures] = useState<StudioFeatures>(DEFAULT_FEATURES);
  const [brand, setBrand] = useState<BrandKit>(EMPTY_BRAND);
  const [tracks, setTracks] = useState<StudioMusicTrack[]>([]);
  const [out, setOut] = useState<OutputSize | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<RenderJob | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [recording, setRecording] = useState<Recording | null>(null);
  const [voiceUploading, setVoiceUploading] = useState(false);
  /** One <audio> per music / voiceover piece, played by the preview. */
  const audioPool = useRef<Map<string, HTMLAudioElement>>(new Map());
  const stageRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const blurRef = useRef<HTMLCanvasElement>(null);
  const dipRef = useRef<HTMLDivElement>(null);
  const els = useRef<Record<string, MediaEl | null>>({});
  const boxes = useRef<Record<string, Box>>({});
  const drag = useRef<{
    tap?: boolean;
    x: number;
    y: number;
    id?: string;
    dx: number;
    dy: number;
  } | null>(null);
  const raf = useRef(0);
  const lastUi = useRef(0);
  const recRef = useRef<MediaRecorder | null>(null);

  const lay = useMemo<TimelineLayout>(() => layout(doc.clips), [doc.clips]);

  const layRef = useRef<TimelineLayout>(lay);
  layRef.current = lay;
  docRef.current = doc;
  const total = lay.total;
  const maxSeconds =
    doc.quality === "HD"
      ? features.maxHdVideoSeconds
      : features.maxVideoSeconds;

  useEffect(() => {
    ensureFonts();
    dispatch(fetchStudioFeatures())
      .unwrap()
      .then((f: Partial<StudioFeatures>) =>
        setFeatures({ ...DEFAULT_FEATURES, ...f }),
      )
      .catch(() => undefined);
    dispatch(fetchStudioBrandKit())
      .unwrap()
      .then(setBrand)
      .catch(() => undefined);
    dispatch(fetchStudioMusic())
      .unwrap()
      .then(setTracks)
      .catch(() => setTracks([]));
  }, [dispatch]);

  // ------------------------------------------------------------ opened with media / music

  const pendingTracks = useRef<StudioMusicTrack[]>(start?.tracks ?? []);
  useEffect(() => {
    if (start?.media?.length) void addClips(start.media);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // music waits for the first clip (it fills the video's length)
  useEffect(() => {
    if (!doc.clips.length || !pendingTracks.current.length) return;
    const list = pendingTracks.current;
    pendingTracks.current = [];
    addAudios(list, "music");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.clips.length]);

  // ------------------------------------------------------------ document + undo

  const pushHistory = useCallback((d: VideoDoc) => {
    const h = hist.current;
    const snap = JSON.stringify(d);
    if (h.stack[h.index] === snap) return;
    h.stack = h.stack.slice(0, h.index + 1);
    h.stack.push(snap);
    if (h.stack.length > HISTORY_LIMIT) h.stack.shift();
    h.index = h.stack.length - 1;
    setCanUndo(h.index > 0);
    setCanRedo(false);
  }, []);

  const commitDoc = useCallback(
    (next: VideoDoc, commit = true) => {
      docRef.current = next;
      setDocState(next);
      if (commit) {
        const h = hist.current;
        if (h.timer !== undefined) clearTimeout(h.timer);
        h.timer = setTimeout(() => pushHistory(next), 350);
      }
    },
    [pushHistory],
  );

  type VideoDocPatch = Partial<VideoDoc> | ((d: VideoDoc) => VideoDoc);

  const setDoc = useCallback(
    (next: VideoDocPatch, commit = true) => {
      const current = docRef.current;
      const updated: VideoDoc =
        typeof next === "function" ? next(current) : { ...current, ...next };
      commitDoc(updated, commit);
    },
    [commitDoc],
  );

  function undo(step: 1 | -1) {
    const h = hist.current;
    if (h.timer !== undefined) clearTimeout(h.timer);
    const next = h.index + step;
    if (next < 0 || next >= h.stack.length) return;
    h.index = next;
    const d = JSON.parse(h.stack[next]) as VideoDoc;
    docRef.current = d;
    setDocState(d);
    setCanUndo(next > 0);
    setCanRedo(next < h.stack.length - 1);
  }

  const updateClip = useCallback(
    (id: string, patch: Partial<Clip>, commit = true) => {
      setDoc(
        (d) => ({
          ...d,
          clips: d.clips.map((c) => (c.id === id ? { ...c, ...patch } : c)),
        }),
        commit,
      );
    },
    [setDoc],
  );

  // ------------------------------------------------------------ music and voiceover pieces

  function addAudios(list: StudioMusicTrack[], role: AudioRole) {
    if (!list.length) return;
    const d = docRef.current;
    const max = features.maxAudios ?? 16;
    if (d.audios.length + list.length > max) {
      setError(`A video can have up to ${max} music and voiceover pieces.`);
      return;
    }
    const L = layRef.current.total;
    const background =
      role === "music" &&
      list.length === 1 &&
      !d.audios.some((a) => a.role === "music");
    let at = tRef.current >= L - 0.5 ? 0 : tRef.current;
    const fresh: AudioItem[] = list.map((track) => {
      const item = newAudioItem(track, role, at, L);
      if (role === "music" && !background) {
        // placed one after another, each played once
        const len = item.sourceDuration || L;
        Object.assign(item, {
          at: r1(Math.min(at, Math.max(0, L - 0.5))),
          loop: false,
          toEnd: false,
          dur: r1(Math.max(0.5, Math.min(len, L - Math.min(at, L - 0.5)))),
          fadeOut: 0,
        });
      }
      at = Math.min(L - 0.5, item.at + item.dur);
      return item;
    });
    commitDoc({ ...d, audios: [...d.audios, ...fresh] });
    setSel({ type: "audio", id: fresh[fresh.length - 1].id });
  }

  const updateAudio = useCallback(
    (id: string, patch: Partial<AudioItem>, commit = true) => {
      setDoc(
        (d) => ({
          ...d,
          audios: d.audios.map((a) => (a.id === id ? { ...a, ...patch } : a)),
        }),
        commit,
      );
    },
    [setDoc],
  );

  function removeAudio(id: string) {
    setDoc((d) => ({ ...d, audios: d.audios.filter((a) => a.id !== id) }));
    setSel(null);
  }

  const updateLayer = useCallback(
    (id: string, patch: LayerPatch, commit = true) => {
      setDoc(
        (d) => ({
          ...d,
          layers: d.layers.map((l) => {
            if (l.id !== id) return l;

            if (l.kind === "text") {
              return {
                ...l,
                ...patch,
                kind: "text" as const,
              };
            }

            return {
              ...l,
              ...patch,
              kind: "image" as const,
            };
          }),
        }),
        commit,
      );
    },
    [setDoc],
  );
  // ------------------------------------------------------------ clips

  function addBlankClip(color: string, at?: number) {
    setError(null);
    const d = docRef.current;
    if (d.clips.length >= features.maxClips) {
      setError(`A video can have up to ${features.maxClips} clips.`);
      return;
    }
    // Use the current output size if known, else a sane default.
    const w = out?.width ?? 1080;
    const h = out?.height ?? 1920;
    const url = solidColorDataUrl(color, w, h);

    const item: GalleryItem = {
      id: `blank-${uid("b")}`, // not a real Gallery id yet
      mediaType: "IMAGE",
      mediaUrl: url,
    };
    const clip: Clip = {
      ...newClip(item, { duration: 0, width: w, height: h }),
      blank: true,
    };

    const pos = at ?? d.clips.length;
    const clips = [...d.clips.slice(0, pos), clip, ...d.clips.slice(pos)];
    const name = d.clips.length ? d.name : "Blank canvas";
    commitDoc({ ...d, clips, name });
    setSel({ type: "clip", id: clip.id });
  }

  async function addClips(items: MediaItem[], at?: number) {
    setAdding(true);
    setError(null);
    try {
      const metas = await Promise.all(items.map(probe));
      const fresh: Clip[] = items.map((it, i) => {
        const meta = metas[i];
        if (!meta) {
          throw new Error(
            `“${it.templateName || it.fileName || "This video"}” could not be read in the browser. Try an MP4 file.`,
          );
        }
        return newClip(it, meta);
      });
      const d = docRef.current;
      if (d.clips.length + fresh.length > features.maxClips) {
        setError(`A video can have up to ${features.maxClips} clips.`);
        return;
      }
      const pos = at ?? d.clips.length;
      const clips = [...d.clips.slice(0, pos), ...fresh, ...d.clips.slice(pos)];
      const first = items[0];
      const name = d.clips.length
        ? d.name
        : `${(first.templateName || first.fileName || "My video").replace(/\.[a-z0-9]+$/i, "")} edit`.slice(
            0,
            120,
          );
      commitDoc({ ...d, clips, name });
      setSel({ type: "clip", id: fresh[0].id });
    } catch (e) {
      setError(errorMessage(e, "Could not add this media."));
    } finally {
      setAdding(false);
    }
  }

  function removeClip(id: string) {
    setDoc((d) => ({ ...d, clips: d.clips.filter((c) => c.id !== id) }));
    setSel(null);
  }

  function duplicateClip(id: string) {
    setDoc((d) => {
      const k = d.clips.findIndex((c) => c.id === id);
      if (k < 0 || d.clips.length >= features.maxClips) return d;
      const copy: Clip = { ...d.clips[k], id: uid("c") };
      return {
        ...d,
        clips: [...d.clips.slice(0, k + 1), copy, ...d.clips.slice(k + 1)],
      };
    });
  }

  function moveClip(from: number, to: number) {
    setDoc((d) => {
      if (to < 0 || to >= d.clips.length || from === to) return d;
      const clips = [...d.clips];
      const [c] = clips.splice(from, 1);
      clips.splice(to, 0, c);
      return { ...d, clips };
    });
  }

  /** Split the selected music/voiceover piece, or the selected clip (or the one under the playhead), at the playhead. */
  function split() {
    if (sel?.type === "audio") {
      const d = docRef.current;
      const k = d.audios.findIndex((a) => a.id === sel.id);
      const parts = k >= 0 ? splitAudio(d.audios[k], tRef.current) : null;
      if (!parts) {
        setError(
          "Move the playhead inside the selected sound to split it.",
        );
        return;
      }
      const audios = [...d.audios];
      audios.splice(k, 1, ...parts);
      commitDoc({ ...d, audios });
      setSel({ type: "audio", id: parts[1].id });
      return;
    }
    const f = frameAt(layRef?.current, tRef.current);
    if (!f) return;
    const d = docRef.current;
    let k =
      sel?.type === "clip" ? d.clips.findIndex((c) => c.id === sel.id) : f.k;
    if (k < 0) k = f.k;
    const item = layRef.current.items[k];
    const local = tRef.current - item.start;
    const next = splitAt(d.clips, k, local);
    if (!next) {
      setError(
        "Move the playhead inside the clip (at least half a second from its ends) to split it.",
      );
      return;
    }
    commitDoc({ ...d, clips: next });
    setSel({ type: "clip", id: next[k + 1].id });
  }

  // ------------------------------------------------------------ layers

  function addLayer(layer: VideoLayer) {
    const d = docRef.current;
    if (d.layers.length >= 20) {
      setError("Use at most 20 text, sticker and picture layers.");
      return;
    }
    const start = tRef.current >= total - 0.5 ? 0 : r1(tRef.current);
    const l: VideoLayer = {
      ...layer,
      start,
      end: Math.max(
        start + 0.5,
        Math.min(total, start + Math.max(3, total - start)),
      ),
    };
    commitDoc({ ...d, layers: [...d.layers, l] });
    setSel({ type: "layer", id: l.id });
    if (l.kind === "image" && l.assetUrl) {
      layerPicture(l.assetUrl)
        .promise.then(() => paint(tRef.current))
        .catch(() => undefined);
    } else if (l.kind === "text") {
      loadFonts([l.font]).then(() => paint(tRef.current));
    }
  }

  function removeLayer(id: string) {
    setDoc((d) => ({ ...d, layers: d.layers.filter((l) => l.id !== id) }));
    setSel(null);
  }

  // keep layers inside the video when it gets shorter
  useEffect(() => {
    if (!total) return;
    const needs = docRef.current.layers.some((l) => l.end > total + 0.01);
    if (needs) {
      setDoc(
        (d) => ({
          ...d,
          layers: d.layers.map((l) => {
            const end = Math.min(l.end, total);
            return {
              ...l,
              end,
              start: Math.min(l.start, Math.max(0, end - 0.5)),
            };
          }),
        }),
        false,
      );
    }
    // music set to play to the end follows the video's length; nothing plays past the end
    const fits = (a: AudioItem): AudioItem => {
      const at = Math.min(a.at, Math.max(0, total - 0.2));
      const room = Math.max(0.2, total - at);
      const dur = a.toEnd
        ? Math.min(room, audioMaxDur(a))
        : Math.min(a.dur, room);
      return at === a.at && dur === a.dur ? a : { ...a, at: r1(at), dur: r1(dur) };
    };
    if (docRef.current.audios.some((a) => fits(a) !== a)) {
      setDoc((d) => ({ ...d, audios: d.audios.map(fits) }), false);
    }
    if (tRef.current > total) seek(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  // ------------------------------------------------------------ output size

  const first = doc.clips[0];
  useEffect(() => {
    if (!first) {
      setOut(null);
      return undefined;
    }
    let cancelled = false;
    dispatch(
      fetchStudioOutputSize({
        aspect: doc.aspect,
        width: first.width || 1080,
        height: first.height || 1920,
        quality: doc.quality,
      }),
    )
      .unwrap()
      .then((s) => {
        if (!cancelled) setOut(s);
      })
      .catch(() => {
        if (!cancelled)
          setOut(localSize(doc.aspect, first.width, first.height, doc.quality));
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, doc.aspect, doc.quality, first]);

  // ------------------------------------------------------------ preview engine

  /** Draws the frame at time tt: which clips show, their transition, filters, blur, sound and overlays. */
  const paint = useCallback((tt: number) => {
    const d = docRef.current;
    const L = layRef.current;
    const f = frameAt(L, tt);
    const isPlaying = playRef.current !== null;
    const fitClass =
      d.aspect === "ORIGINAL" || d.fit === "FIT" ? "contain" : "cover";

    d.clips.forEach((c, i) => {
      const el = els.current[c.id];
      if (!el) return;
      const vid = el instanceof HTMLVideoElement ? el : null;
      const roleA = f?.k === i;
      const roleB = f?.next?.k === i;

      if (!f || (!roleA && !roleB)) {
        el.style.visibility = "hidden";
        if (vid && !vid.paused) vid.pause();
        return;
      }

      const local = roleA || !f.next ? f.local : f.next.local;
      const tr =
        f.next && f.type && f.progress !== undefined
          ? transitionStyles(f.type, f.progress)
          : null;
      const st: SideStyle = tr ? (roleA ? tr.a : tr.b) : {};

      el.style.visibility = "visible";
      el.style.zIndex = roleB ? "2" : "1";
      el.style.objectFit = fitClass;
      el.style.opacity = String(st.opacity ?? 1);
      el.style.clipPath = st.clipPath ?? "none";
      el.style.setProperty("-webkit-mask-image", st.WebkitMaskImage ?? "none");
      el.style.setProperty("mask-image", st.maskImage ?? "none");
      const look = filterCss(c.filter);
      el.style.filter =
        [look !== "none" ? look : "", st.filter ?? ""].join(" ").trim() ||
        "none";
      const kb =
        c.image && c.kenBurns
          ? ` scale(${1 + 0.12 * (local / Math.max(0.1, c.duration))})`
          : "";
      el.style.transform = `${st.transform ?? ""}${kb}`.trim() || "none";

      if (vid) {
        const want = sourceTime(c, local);
        vid.playbackRate = c.speed || 1;
        vid.muted = c.muted || c.volume === 0;
        vid.volume = clamp(c.muted ? 0 : c.volume, 0, 1);
        if (isPlaying) {
          if (vid.paused) {
            if (Math.abs(vid.currentTime - want) > 0.08) vid.currentTime = want;
            void vid.play().catch(() => undefined);
          } else if (Math.abs(vid.currentTime - want) > 0.35) {
            vid.currentTime = want;
          }
        } else {
          if (!vid.paused) vid.pause();
          if (Math.abs(vid.currentTime - want) > 0.03) vid.currentTime = want;
        }
      }
    });

    // dip to black/white
    const dip = dipRef.current;
    if (dip) {
      const tr =
        f?.next && f.type && f.progress !== undefined
          ? transitionStyles(f.type, f.progress).dip
          : null;
      dip.style.display = tr ? "block" : "none";
      if (tr) {
        dip.style.background = tr.background;
        dip.style.opacity = String(tr.opacity);
      }
    }

    // blurred background (fit mode)
    const bc = blurRef.current;
    if (
      bc &&
      f &&
      d.aspect !== "ORIGINAL" &&
      d.fit === "FIT" &&
      d.background === "BLUR"
    ) {
      const c = d.clips[f.k];
      const el = c ? els.current[c.id] : undefined;
      const ctx = bc.getContext("2d");
      if (el && ctx) {
        try {
          ctx.drawImage(el, 0, 0, bc.width, bc.height);
        } catch {
          /* media not ready yet */
        }
      }
    }

    // overlays
    const cv = overlayRef.current;
    const box = stageRef.current;
    if (cv && box) {
      const r = box.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      if (cv.width !== Math.round(r.width * dpr)) {
        cv.width = Math.round(r.width * dpr);
        cv.height = Math.round(r.height * dpr);
      }
      const ctx = cv.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, cv.width, cv.height);
        boxes.current = {};
        const selId =
          selRef.current?.type === "layer" ? selRef.current.id : null;
        for (const l of d.layers) {
          const visible = tt >= l.start - 0.001 && tt <= l.end + 0.001;
          if (!visible && l.id !== selId) continue;
          ctx.save();
          ctx.globalAlpha = visible ? 1 : 0.3;
          boxes.current[l.id] = drawLayer(
            ctx,
            l,
            cv.width,
            cv.height,
            visible ? animState(l, tt) : undefined,
          );
          ctx.restore();
          if (l.id === selId) {
            const b = boxes.current[l.id];
            ctx.setLineDash([6 * dpr, 4 * dpr]);
            ctx.lineWidth = 1.5 * dpr;
            ctx.strokeStyle = "#25d366";
            ctx.strokeRect(
              b.x - 6 * dpr,
              b.y - 6 * dpr,
              b.w + 12 * dpr,
              b.h + 12 * dpr,
            );
            ctx.setLineDash([]);
          }
        }
      }
    }

    // music and voiceover pieces (volume, fades, music lowered while a voice plays)
    const voiceOn =
      d.duck && d.audios.some((a) => a.role === "voice" && audioSourceTime(a, tt) !== null);
    for (const a of d.audios) {
      const el = audioPool.current.get(a.id);
      if (!el) continue;
      const want = audioSourceTime(a, tt);
      if (want === null || !isPlaying) {
        if (!el.paused) el.pause();
        continue;
      }
      const duck = voiceOn && a.role === "music" ? 0.3 : 1;
      el.volume = clamp(audioGainAt(a, tt) * duck, 0, 1);
      if (el.paused) {
        el.currentTime = want;
        void el.play().catch(() => undefined);
      } else if (Math.abs(el.currentTime - want) > 0.35) {
        el.currentTime = want;
      }
    }
  }, []);

  const tick = useCallback(() => {
    const p = playRef.current;
    if (!p) return;
    let tt = p.from + (performance.now() - p.perf) / 1000;
    const L = layRef.current;
    if (tt >= L.total) {
      tt = L.total;
      stop();
      if (recRef.current) stopRecording();
    }
    tRef.current = tt;
    paint(tt);
    const now = performance.now();
    if (now - lastUi.current > 60 || !playRef.current) {
      lastUi.current = now;
      setT(tt);
    }
    if (playRef.current) raf.current = requestAnimationFrame(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paint]);

  function play(from: number = tRef.current) {
    if (!layRef.current.total) return;
    const start = from >= layRef.current.total - 0.05 ? 0 : from;
    tRef.current = start;
    playRef.current = { perf: performance.now(), from: start };
    setPlaying(true);
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(tick);
  }

  function stop() {
    audioPool.current.forEach((el) => el.pause());
    playRef.current = null;
    setPlaying(false);
    cancelAnimationFrame(raf.current);
    Object.values(els.current).forEach((el) => {
      if (el instanceof HTMLVideoElement) el.pause();
    });
    setT(tRef.current);
  }

  const togglePlay = () => (playRef.current ? stop() : play());

  function seek(tt: number) {
    const v = clamp(tt, 0, layRef.current.total || 0);
    tRef.current = v;
    setT(v);
    if (playRef.current) playRef.current = { perf: performance.now(), from: v };
    paint(v);
  }

  // one <audio> element per piece (pieces split from one track share nothing, so each keeps its own position)
  useEffect(() => {
    const pool = audioPool.current;
    const seen = new Set<string>();
    for (const a of doc.audios) {
      seen.add(a.id);
      let el = pool.get(a.id);
      if (!el || el.dataset.src !== a.url) {
        el?.pause();
        el = new Audio(a.url);
        el.preload = "auto";
        el.dataset.src = a.url;
        pool.set(a.id, el);
      }
      el.loop = a.loop;
    }
    for (const [id, el] of pool) {
      if (!seen.has(id)) {
        el.pause();
        pool.delete(id);
      }
    }
  }, [doc.audios]);

  useEffect(() => {
    const pool = audioPool.current;
    return () => {
      pool.forEach((el) => el.pause());
      pool.clear();
    };
  }, []);

  // repaint whenever the edit changes
  useEffect(() => {
    paint(tRef.current);
  });
  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  // ------------------------------------------------------------ dragging layers on the preview

  function hit(e: ReactPointerEvent<HTMLCanvasElement>) {
    const c = overlayRef.current;
    if (!c) return null;
    const r = c.getBoundingClientRect();
    const k = c.width / r.width;
    const px = (e.clientX - r.left) * k;
    const py = (e.clientY - r.top) * k;
    const ids = Object.keys(boxes.current).reverse();
    const id = ids.find((key) => {
      const b = boxes.current[key];
      return (
        px >= b.x - 10 &&
        px <= b.x + b.w + 10 &&
        py >= b.y - 10 &&
        py <= b.y + b.h + 10
      );
    });
    return { id, px, py, c };
  }

  function onPointerDown(e: ReactPointerEvent<HTMLCanvasElement>) {
    const h = hit(e);
    e.currentTarget.setPointerCapture(e.pointerId);
    if (!h || !h.id) {
      drag.current = { tap: true, x: e.clientX, y: e.clientY, dx: 0, dy: 0 };
      return;
    }
    const l = docRef.current.layers.find((x) => x.id === h.id);
    if (!l) return;
    setSel({ type: "layer", id: l.id });
    setPanel(l.kind === "image" || l.sticker ? "elements" : "text");
    drag.current = {
      id: l.id,
      dx: l.x * h.c.width - h.px,
      dy: l.y * h.c.height - h.py,
      x: 0,
      y: 0,
    };
  }

  function onPointerMove(e: ReactPointerEvent<HTMLCanvasElement>) {
    const d = drag.current;
    if (!d || d.tap || !d.id) return;
    const h = hit(e);
    if (!h) return;
    updateLayer(
      d.id,
      {
        x: clamp((h.px + d.dx) / h.c.width, 0.02, 0.98),
        y: clamp((h.py + d.dy) / h.c.height, 0.02, 0.98),
      },
      false,
    );
  }

  function onPointerUp(e: ReactPointerEvent<HTMLCanvasElement>) {
    const d = drag.current;
    drag.current = null;
    if (
      d?.tap &&
      Math.abs(e.clientX - d.x) < 6 &&
      Math.abs(e.clientY - d.y) < 6
    ) {
      setSel(null);
      togglePlay();
    } else if (d?.id) {
      pushHistory(docRef.current);
    }
  }

  // ------------------------------------------------------------ voiceover recording

  async function startRecording() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      const type =
        ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find(
          (x) => MediaRecorder.isTypeSupported(x),
        ) ?? "";
      const rec = new MediaRecorder(
        stream,
        type ? { mimeType: type } : undefined,
      );
      const chunks: Blob[] = [];
      const startT = tRef.current >= total - 0.5 ? 0 : tRef.current;
      rec.ondataavailable = (ev: BlobEvent) => {
        if (ev.data.size) chunks.push(ev.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        setRecording({
          status: "done",
          blob,
          url: URL.createObjectURL(blob),
          startT,
        });
      };
      recRef.current = rec;
      setRecording({ status: "live", startT, since: Date.now() });
      rec.start(250);
      seek(startT);
      play(startT);
    } catch (e) {
      const isDenied =
        e instanceof DOMException && e.name === "NotAllowedError";
      setError(
        isDenied
          ? "Allow microphone access in your browser to record a voiceover."
          : `Could not start recording: ${errorMessage(e, "unknown error")}`,
      );
    }
  }

  function stopRecording() {
    const rec = recRef.current;
    recRef.current = null;
    if (rec && rec.state !== "inactive") rec.stop();
    stop();
  }

  function getAudioDuration(url: string): Promise<number> {
    return new Promise((resolve) => {
      const audio = document.createElement("audio");
      audio.preload = "metadata";
      audio.src = url;

      const cleanup = () => {
        audio.removeAttribute("src");
        audio.load();
      };

      audio.onloadedmetadata = () => {
        if (audio.duration === Infinity || Number.isNaN(audio.duration)) {
          // Chrome bug: force it to compute the real duration
          audio.currentTime = 1e101;
          audio.ontimeupdate = () => {
            audio.ontimeupdate = null;
            const d = audio.duration;
            cleanup();
            resolve(Number.isFinite(d) && d > 0 ? d : 0);
          };
        } else {
          const d = audio.duration;
          cleanup();
          resolve(Number.isFinite(d) && d > 0 ? d : 0);
        }
      };

      audio.onerror = () => {
        cleanup();
        resolve(0);
      };
    });
  }

  async function webmBlobToWav(blob: Blob): Promise<Blob> {
    const arrayBuffer = await blob.arrayBuffer();
    const ctx = new AudioContext();
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

    const numChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const length = audioBuffer.length * numChannels * 2 + 44;
    const buffer = new ArrayBuffer(length);
    const view = new DataView(buffer);

    const writeStr = (offset: number, s: string) => {
      for (let i = 0; i < s.length; i++)
        view.setUint8(offset + i, s.charCodeAt(i));
    };

    writeStr(0, "RIFF");
    view.setUint32(4, 36 + audioBuffer.length * numChannels * 2, true);
    writeStr(8, "WAVE");
    writeStr(12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * 2, true);
    view.setUint16(32, numChannels * 2, true);
    view.setUint16(34, 16, true);
    writeStr(36, "data");
    view.setUint32(40, audioBuffer.length * numChannels * 2, true);

    let offset = 44;
    for (let i = 0; i < audioBuffer.length; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        const sample = Math.max(
          -1,
          Math.min(1, audioBuffer.getChannelData(ch)[i]),
        );
        view.setInt16(
          offset,
          sample < 0 ? sample * 0x8000 : sample * 0x7fff,
          true,
        );
        offset += 2;
      }
    }

    return new Blob([buffer], { type: "audio/wav" });
  }

  async function keepRecording() {
    if (recording?.status !== "done") return;

    const r = recording;
    setSubmitting(true);

    try {
      console.log(
        "WebM supported:",
        document.createElement("audio").canPlayType("audio/webm"),
      );

      console.log(
        "WebM Opus supported:",
        document
          .createElement("audio")
          .canPlayType('audio/webm; codecs="opus"'),
      );

      console.log("Blob type:", r.blob.type);
      console.log("Blob size:", r.blob.size);
      console.log("Blob is empty:", r.blob.size === 0);

      // Get duration from the recorded audio before uploading.
      const localDuration = await getAudioDuration(r.url);

      console.log("Local audio duration:", localDuration);

      // Upload the recorded audio.
      const wavBlob = await webmBlobToWav(r.blob);
      const track = await dispatch(uploadStudioVoiceover(wavBlob)).unwrap();

      console.log("Uploaded voiceover:", track);

      // Prefer the local duration, then the backend duration.
      const durationSec =
        localDuration > 0 ? localDuration : Number(track.durationSec) || 0;

      console.log("Final audio duration:", durationSec);

      const d = docRef.current;
      const piece = newAudioItem(
        { id: String(track.id), url: track.url, title: "Voiceover", durationSec },
        "voice",
        r.startT,
        layRef.current.total,
      );
      commitDoc({ ...d, audios: [...d.audios, piece] });
      setSel({ type: "audio", id: piece.id });

      setRecording(null);
    } catch (e) {
      console.error("Voiceover upload failed:", e);

      setError(errorMessage(e, "Could not save the voiceover."));
    } finally {
      setSubmitting(false);
    }
  }

  /** Uploads voiceover files (one or many) and places them one after another from the playhead. */
  async function uploadVoiceFiles(files: File[]) {
    if (!files.length) return;
    setError(null);
    setVoiceUploading(true);
    const done: StudioMusicTrack[] = [];
    const failed: string[] = [];
    try {
      for (const file of files) {
        const localUrl = URL.createObjectURL(file);
        try {
          // eslint-disable-next-line no-await-in-loop
          const localDuration = await getAudioDuration(localUrl);
          // eslint-disable-next-line no-await-in-loop
          const track = await dispatch(uploadStudioVoiceover(file)).unwrap();
          const durationSec =
            localDuration > 0 ? localDuration : Number(track.durationSec) || 0;
          done.push({
            id: String(track.id),
            url: track.url,
            durationSec,
            title: file.name.replace(/\.[^.]+$/, ""),
          });
        } catch (e) {
          failed.push(`${file.name}: ${errorMessage(e, "upload failed")}`);
        } finally {
          URL.revokeObjectURL(localUrl);
        }
      }
      addAudios(done, "voice");
      if (failed.length) setError(`Some files were not added. ${failed.join(" · ")}`);
    } finally {
      setVoiceUploading(false);
    }
  }

  // ------------------------------------------------------------ keyboard

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target =
        e.target instanceof HTMLElement ? e.target.tagName.toLowerCase() : "";
      if (target === "input" || target === "textarea" || target === "select")
        return;
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (mod && key === "z") {
        e.preventDefault();
        undo(e.shiftKey ? 1 : -1);
        return;
      }
      if (mod && key === "y") {
        e.preventDefault();
        undo(1);
        return;
      }
      if (e.code === "Space") {
        e.preventDefault();
        togglePlay();
        return;
      }
      if (key === "s" && !mod) {
        e.preventDefault();
        split();
        return;
      }
      if (e.key === "ArrowLeft" && !playRef.current) {
        e.preventDefault();
        seek(tRef.current - (e.shiftKey ? 1 : 0.1));
        return;
      }
      if (e.key === "ArrowRight" && !playRef.current) {
        e.preventDefault();
        seek(tRef.current + (e.shiftKey ? 1 : 0.1));
        return;
      }
      if ((e.key === "Delete" || e.key === "Backspace") && sel) {
        e.preventDefault();
        if (sel.type === "layer") removeLayer(sel.id);
        if (sel.type === "clip") removeClip(sel.id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // ------------------------------------------------------------ export

  // async function render() {
  //   setError(null);
  //   const d = docRef.current;
  //   if (!d.clips.length || !out) return;
  //   if (total < 1) {
  //     setError("The video must be at least 1 second long.");
  //     return;
  //   }
  //   if (total > maxSeconds) {
  //     setError(
  //       d.quality === "HD"
  //         ? `HD videos can be up to ${Math.round(maxSeconds / 60)} minutes. Trim or remove some clips.`
  //         : `WhatsApp videos can be up to ${Math.round(maxSeconds / 60)} minutes (16 MB). Trim some clips or choose HD.`,
  //     );
  //     return;
  //   }
  //   stop();
  //   setSubmitting(true);
  //   try {
  //     await loadFonts(
  //       d.layers
  //         .filter((l): l is TextLayer => l.kind === "text")
  //         .map((l) => l.font)
  //         .filter(Boolean),
  //     );
  //     const usable = d.layers.filter(
  //       (l) => (l.kind === "image" || (l.text ?? "").trim()) && l.end > l.start,
  //     );
  //     const pngs = await layersToPngs(usable, out.width, out.height);
  //     const spec: RenderSpec = buildSpec(d, usable);
  //     const rendered = await dispatch(
  //       renderStudioVideo({ spec,  overlays: [], }),
  //     ).unwrap();
  //     setJob(rendered);
  //   } catch (e) {
  //     setError(errorMessage(e, "Could not start the render."));
  //   } finally {
  //     setSubmitting(false);
  //   }
  // }

  async function render() {
    setError(null);

    const d = docRef.current;

    if (!d.clips.length || !out) return;

    if (total < 1) {
      setError("The video must be at least 1 second long.");
      return;
    }

    if (total > maxSeconds) {
      setError(
        d.quality === "HD"
          ? `HD videos can be up to ${Math.round(
              maxSeconds / 60,
            )} minutes. Trim or remove some clips.`
          : `WhatsApp videos can be up to ${Math.round(
              maxSeconds / 60,
            )} minutes (16 MB). Trim some clips or choose HD.`,
      );

      return;
    }

    stop();
    setSubmitting(true);

    try {
      const resolvedClips = await Promise.all(
        d.clips.map(async (c) => {
          if (!c.blank) return c;
          const blob = await (await fetch(c.url)).blob();
          const file = new File([blob], "blank-canvas.png", {
            type: "image/png",
          });
          const uploaded = await dispatch(uploadStudioAsset(file)).unwrap();
          return {
            ...c,
            galleryId: uploaded.id,
            url: uploaded.mediaUrl,
            blank: false,
          };
        }),
      );
      const resolvedDoc = { ...d, clips: resolvedClips };
      commitDoc(resolvedDoc, false);
      await loadFonts(
        d.layers
          .filter((l): l is TextLayer => l.kind === "text")
          .map((l) => l.font)
          .filter(Boolean),
      );

      const usable = d.layers.filter(
        (l) => (l.kind === "image" || (l.text ?? "").trim()) && l.end > l.start,
      );

      const spec: RenderSpec = buildSpec(resolvedDoc, usable);
      const overlays = await layersToPngs(usable, out.width, out.height);

      const rendered = await dispatch(
        renderStudioVideo({
          spec,
          overlays,
        }),
      ).unwrap();

      setJob(rendered);
    } catch (e) {
      setError(errorMessage(e, "Could not start the render."));
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    if (!job || job.status === "DONE" || job.status === "FAILED")
      return undefined;
    const timer = setTimeout(() => {
      dispatch(fetchStudioVideoJob(job.id))
        .unwrap()
        .then(setJob)
        .catch(() => setJob({ ...job }));
    }, 1500);
    return () => clearTimeout(timer);
  }, [dispatch, job]);

  // ------------------------------------------------------------ layout

  const ratio = out
    ? out.width / out.height
    : doc.aspect === "SQUARE_1_1"
      ? 1
      : doc.aspect === "LANDSCAPE_16_9"
        ? 16 / 9
        : 9 / 16;

  if (!doc.clips.length) {
    return (
      <div className="fixed inset-0 z-[900] flex flex-col bg-st-stage">
        <header className="flex h-[58px] flex-none items-center justify-between gap-3 bg-[radial-gradient(120%_140%_at_0%_0%,#0288D1_0%,#014B73_45%,#0E1C2B_100%)] pl-2 pr-3 text-white">
          <div className="flex min-w-0 items-center gap-2">
            <button
              className={ICON_BTN_DARK}
              onClick={onClose}
              aria-label="Back to Studio"
            >
              <Icon name="back" />
            </button>
            <span className="text-[15px] font-semibold">New video</span>
          </div>
        </header>
        <StartPicker
          onPick={(it) => addClips([it])}
          onPickBlank={(color) => addBlankClip(color)}
          busy={adding}
          error={error}
        />
      </div>
    );
  }

  const ed: VideoEditorApi = {
    doc,
    setDoc,
    updateClip,
    updateLayer,
    addClips,
    removeClip,
    duplicateClip,
    moveClip,
    split,
    addLayer,
    removeLayer,
    sel,
    setSel,
    panel,
    setPanel,
    features,
    brand,
    tracks,
    setTracks,
    total,
    t,
    lay,
    maxSeconds,
    adding,
    recording,
    startRecording,
    stopRecording,
    keepRecording,
    discardRecording: () => setRecording(null),
    uploadVoiceFiles,
    voiceUploading,
    submitting,
    seek,
    playing,
    addBlankClip,
    addAudios,
    updateAudio,
    removeAudio,
  };

  return (
    <div className="fixed inset-0 z-[900] flex flex-col bg-st-stage">
      <header className="flex relative h-[58px] flex-none items-center justify-between gap-3 bg-[#0b1220]  py-0 pl-2 pr-3 text-white">
        <div className="pointer-events-none absolute -top-24 -left-16 w-72 h-72 rounded-full bg-brand-main/25 blur-3xl" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-white/10 via-white/[0.04] to-transparent" />
        <div className="flex min-w-0 items-center gap-2">
          <button
            className={ICON_BTN_DARK}
            onClick={onClose}
            aria-label="Back to Studio"
            title="Back to Studio"
          >
            <Icon name="back" />
          </button>
          <input
            className="w-[clamp(120px,22vw,300px)] rounded-lg border border-transparent bg-transparent px-[9px] py-[7px] text-[15px] font-semibold text-white hover:border-white/20 focus:border-white/35 focus:bg-white/10 focus:outline-none"
            value={doc.name}
            maxLength={120}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setDoc((d) => ({ ...d, name: e.target.value }))
            }
            aria-label="Video name"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            className={ICON_BTN_DARK}
            disabled={!canUndo}
            onClick={() => undo(-1)}
            aria-label="Undo"
            title="Undo (Ctrl+Z)"
          >
            <Icon name="undo" />
          </button>
          <button
            className={ICON_BTN_DARK}
            disabled={!canRedo}
            onClick={() => undo(1)}
            aria-label="Redo"
            title="Redo (Ctrl+Y)"
          >
            <Icon name="redo" />
          </button>
        </div>
        <div className="flex min-w-0 items-center gap-2">
          {out && (
            <span className="text-[13px] tabular-nums text-[#a9c5ba] max-[820px]:hidden">
              {out.width} × {out.height} · {fmt(total)}
              {doc.quality === "HD" ? " · HD" : ""}
            </span>
          )}
          <button
            className={cx(
              BTN,
              "border-web-primary bg-web-primary text-[#062b1c] hover:border-[#3ee07c] hover:bg-[#3ee07c]",
            )}
            disabled={!out || submitting || total > maxSeconds || !!recording}
            onClick={render}
          >
            <Icon name="sparkle" size={18} />{" "}
            {submitting ? "Preparing…" : "Export video"}
          </button>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-[76px_332px_minmax(0,1fr)] max-[1100px]:grid-cols-[68px_270px_minmax(0,1fr)] max-[900px]:grid-cols-[64px_minmax(0,1fr)] max-[900px]:grid-rows-[minmax(0,1fr)_auto] max-[820px]:block max-[820px]:overflow-y-auto">
        <nav
          className="
    flex flex-col gap-0.5 overflow-y-auto bg-st-night p-1.5
    max-[820px]:flex-row
    max-[820px]:items-stretch
    max-[820px]:gap-1
    max-[820px]:overflow-x-auto
    max-[820px]:overflow-y-hidden
    max-[820px]:p-1
    max-[820px]:scrollbar-thin
  "
          aria-label="Video tools"
        >
          {VIDEO_RAIL.map(
            ([key, label, icon]: readonly [
              VideoPanelKey,
              string,
              IconName,
            ]) => (
              <button
                key={key}
                type="button"
                aria-pressed={panel === key}
                onClick={() => setPanel(key)}
                className={cx(
                  // Desktop
                  "flex shrink-0 flex-col items-center justify-center gap-1 rounded-[10px] border-0 bg-transparent px-1 py-2.5 text-[11px] font-medium text-[#a9c5ba] transition-colors",
                  "hover:bg-white/5 hover:text-white",

                  // Selected
                  panel === key &&
                    "bg-st-night-2 text-white shadow-[inset_3px_0_0_#25d366]",

                  // Mobile
                  "max-[820px]:min-w-[72px]",
                  "max-[820px]:w-[72px]",
                  "max-[820px]:flex-none",
                  "max-[820px]:py-2",
                  "max-[820px]:text-[10px]",
                  "max-[820px]:shadow-none",

                  // Mobile selected indicator
                  panel === key &&
                    "max-[820px]:shadow-[inset_0_-3px_0_#25d366]",

                  // Very small phones
                  "max-[380px]:min-w-[64px]",
                  "max-[380px]:w-[64px]",
                  "max-[380px]:text-[9px]",
                )}
              >
                <Icon name={icon} size={22} />

                <span className="max-w-full truncate px-0.5">{label}</span>
              </button>
            ),
          )}
        </nav>

        <aside className="overflow-y-auto border-r border-st-line bg-white max-[900px]:max-h-[42vh] max-[900px]:border-r-0 max-[900px]:border-t max-[820px]:max-h-none">
          <VideoPanels ed={ed} />
        </aside>

        <main className="relative flex min-h-0 min-w-0 flex-col max-[820px]:h-[62vh] max-[820px]:flex-none">
          {error && (
            <div className={cx(BANNER, "mx-4 mt-3")} role="alert">
              {error}
              <button
                className="grid h-[30px] w-[30px] place-items-center rounded-lg border-0 bg-transparent text-inherit"
                onClick={() => setError(null)}
                aria-label="Dismiss"
              >
                <Icon name="close" size={16} />
              </button>
            </div>
          )}
          {total > maxSeconds && (
            <div className={cx(BANNER, "mx-4 mt-3")}>
              The video is {fmt(total)} long.{" "}
              {doc.quality === "HD" ? "HD" : "WhatsApp"} videos can be up to{" "}
              {Math.round(maxSeconds / 60)} minutes. Trim or remove clips.
            </div>
          )}

          <div className="grid min-h-0 flex-1 place-items-center overflow-hidden p-5">
            <div
              ref={stageRef}
              className="relative h-[min(100%,640px)] max-w-full overflow-hidden rounded-2xl bg-black shadow-[0_0_0_6px_#10231e,0_30px_60px_-24px_rgba(0,0,0,0.6)] [container-type:size]"
              style={{
                aspectRatio: `${ratio}`,
                background:
                  doc.fit === "FIT" && doc.background !== "BLUR"
                    ? doc.background
                    : "#000",
              }}
            >
              {doc.aspect !== "ORIGINAL" &&
                doc.fit === "FIT" &&
                doc.background === "BLUR" && (
                  <canvas
                    ref={blurRef}
                    width={48}
                    height={Math.round(48 / ratio)}
                    className="absolute inset-0 h-full w-full scale-[1.2] blur-[18px] brightness-[0.72]"
                    aria-hidden="true"
                  />
                )}
              {doc.clips.map((c) =>
                c.image ? (
                  <img
                    key={c.id}
                    ref={(el: HTMLImageElement | null) => {
                      els.current[c.id] = el;
                    }}
                    className="pointer-events-none invisible absolute inset-0 h-full w-full origin-center select-none object-contain"
                    src={c.url}
                    alt=""
                    draggable={false}
                  />
                ) : (
                  <video
                    key={c.id}
                    ref={(el: HTMLVideoElement | null) => {
                      els.current[c.id] = el;
                    }}
                    className="pointer-events-none invisible absolute inset-0 h-full w-full origin-center select-none object-contain"
                    src={c.url}
                    playsInline
                    preload="auto"
                    onLoadedData={() => paint(tRef.current)}
                    onSeeked={() => {
                      if (!playRef.current) paint(tRef.current);
                    }}
                  />
                ),
              )}
              <div
                ref={dipRef}
                className="pointer-events-none absolute inset-0 z-[3] hidden"
              />
              <canvas
                ref={overlayRef}
                className="absolute inset-0 z-[4] h-full w-full touch-none cursor-move"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
              />
              {!playing && !recording && (
                <span
                  className="pointer-events-none absolute left-1/2 top-1/2 z-[5] grid h-[68px] w-[68px] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-[rgba(14,28,24,0.6)] text-white backdrop-blur-[6px]"
                  aria-hidden="true"
                >
                  <Icon name="play" size={30} />
                </span>
              )}
              {recording?.status === "live" && (
                <span className="absolute left-1/2 top-3 z-[6] inline-flex -translate-x-1/2 items-center gap-2 rounded-full bg-[rgba(176,53,31,0.92)] px-3 py-1.5 text-[13px] font-semibold text-white">
                  <span className="h-[9px] w-[9px] animate-st-blink rounded-full bg-white" />{" "}
                  Recording… speak now
                </span>
              )}
            </div>
          </div>

          <VideoTimeline
            ed={ed}
            t={t}
            playing={playing}
            onToggle={togglePlay}
            onSplit={split}
          />
        </main>
      </div>

      {recording?.status === "done" && (
        <Modal
          title="Your voiceover"
          onClose={() => setRecording(null)}
          width={420}
          footer={
            <>
              <button className={BTN} onClick={() => setRecording(null)}>
                Discard
              </button>
              <button
                className={` ${cx(BTN, BTN_ACCENT)} bg-st-green-dark`}
                disabled={submitting}
                onClick={keepRecording}
              >
                {submitting ? "Saving…" : "Use this take"}
              </button>
            </>
          }
        >
          <p className="mb-3 text-[13px] leading-normal text-st-muted">
            Starts at {fmt(recording.startT)} in the video. Listen back before
            you keep it.
          </p>
          <audio src={recording.url} controls className="w-full" />
        </Modal>
      )}

      {job && (
        <RenderDialog job={job} onClose={() => setJob(null)} onDone={onClose} />
      )}
    </div>
  );
}

const r3 = (v: number) => Math.round(v * 1000) / 1000;

function buildSpec(d: VideoDoc, usable: VideoLayer[]): RenderSpec {
  return {
    clips: d.clips.map((c) => ({
      galleryId: c.galleryId,
      trimStart: r1(c.trimStart),
      trimEnd: c.image ? 0 : r1(c.trimEnd),
      duration: r1(c.duration),
      speed: c.speed,
      filter: c.filter,
      volume: c.volume,
      muted: c.muted,
      transition: c.transition,
      transitionDuration: c.transitionDuration,
      kenBurns: c.kenBurns,
    })),
    aspect: d.aspect,
    fit: d.fit,
    background: d.background,
    quality: d.quality,
    // each piece fades on its own
    fadeOut: false,
    duckMusic: d.duck,
    audios: d.audios.map((a) => ({
      trackId: a.trackId,
      role: a.role === "voice" ? "VOICE" : "MUSIC",
      at: r3(a.at),
      from: r3(a.from),
      duration: r3(a.dur),
      loop: a.loop,
      volume: a.volume,
      fadeIn: r3(a.fadeIn),
      fadeOut: r3(a.fadeOut),
    })),
    overlays: usable.map((l) => ({
      start: r1(l.start),
      end: r1(l.end),
      animIn: l.animIn ?? "NONE",
      animOut: l.animOut ?? "NONE",
    })),
    name: d.name.trim() || "My video",
  };
}

interface StartPickerProps {
  onPick: (item: MediaItem) => void;
  onPickBlank: (color: string) => void;
  busy: boolean;
  error: string | null;
}

function StartPicker({ onPick, onPickBlank, busy, error }: StartPickerProps) {
  const [tab, setTab] = useState<MediaKind | "BLANK">("VIDEO");
  return (
    <div className="grid flex-1 items-start justify-items-center overflow-y-auto px-4 py-10">
      <div className="w-[min(820px,100%)] rounded-[20px] bg-white p-7 text-center shadow-[0_20px_50px_-30px_rgba(0,0,0,0.4)]">
        <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-[14px] bg-[linear-gradient(135deg,#f97316,#e11d48)] text-white">
          <Icon name="video" size={28} />
        </span>
        <h1 className="font-['Bricolage_Grotesque',sans-serif] text-[26px] font-bold tracking-[-0.01em]">
          Start your video
        </h1>
        <p className="mx-auto mb-5 mt-2 max-w-[46ch] text-[13px] leading-normal text-st-muted">
          Pick a video or a photo to begin, or start with a blank canvas and
          build it up with text and elements.
        </p>
        <div className="mx-auto mb-3 flex max-w-[480px] gap-0.5 border-b border-st-line">
          {(["VIDEO", "IMAGE", "BLANK"] as const).map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={cx(
                "-mb-px flex-1 border-0 border-b-2 border-transparent bg-transparent px-1.5 py-2.5 text-[13.5px] font-semibold text-st-muted",
                tab === k && "border-b-st-green text-st-ink",
              )}
            >
              {k === "VIDEO"
                ? "Videos"
                : k === "IMAGE"
                  ? "Photos (slideshow)"
                  : "Blank canvas"}
            </button>
          ))}
        </div>
        {busy && <p className={HINT}>Opening…</p>}
        {error && (
          <p className={ERROR_TEXT} role="alert">
            {error}
          </p>
        )}
        {tab === "BLANK" ? (
          <div className="grid gap-3">
            <p className={HINT}>Pick a background colour to start with.</p>
            <div className="mx-auto grid grid-cols-4 gap-2">
              {BLANK_COLORS.map((c) => (
                <button
                  key={c}
                  className="h-12 w-12 cursor-pointer rounded-xl border border-black/10 hover:scale-105"
                  style={{ background: c }}
                  onClick={() => onPickBlank(c)}
                  aria-label={`Start with ${c} background`}
                />
              ))}
            </div>
          </div>
        ) : (
          <MediaPicker key={tab} type={tab} onPick={onPick} busy={busy} />
        )}
      </div>
    </div>
  );
}

interface RenderDialogProps {
  job: RenderJob;
  onClose: () => void;
  onDone: () => void;
}

function RenderDialog({ job, onClose, onDone }: RenderDialogProps) {
  const done = job.status === "DONE";
  const failed = job.status === "FAILED";
  const progress = job.progress ?? 0;
  return (
    <Modal
      title={
        done
          ? "Your video is ready"
          : failed
            ? "The video could not be made"
            : "Making your video"
      }
      onClose={done || failed ? onClose : onDone}
      width={480}
      footer={
        done ? (
          <>
            <button className={BTN} onClick={onClose}>
              Keep editing
            </button>
            <button className={cx(BTN, BTN_ACCENT)} onClick={onDone}>
              Done
            </button>
          </>
        ) : failed ? (
          <button className={cx(BTN, BTN_ACCENT)} onClick={onClose}>
            Back to editing
          </button>
        ) : (
          <button className={BTN} onClick={onDone}>
            Back to Studio
          </button>
        )
      }
    >
      {!done && !failed && (
        <>
          <p className="mb-3 text-[13px] leading-normal text-st-muted">
            {job.status === "QUEUED"
              ? "Waiting for a free slot…"
              : "Joining clips, adding transitions, text and sound, and sizing it for WhatsApp."}{" "}
            You can leave this page; it will appear in your Gallery.
          </p>
          <div
            className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-st-paper"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
          >
            <span
              className="block h-full rounded-[inherit] bg-[linear-gradient(90deg,#0b7a5a,#25d366)] transition-[width] duration-[400ms]"
              style={{ width: `${Math.max(4, progress)}%` }}
            />
          </div>
          <p className="mt-2 font-bold tabular-nums">{progress}%</p>
        </>
      )}
      {done && (
        <>
          {job.outputUrl && (
            <video
              className="mt-2 max-h-[50vh] w-full rounded-xl bg-black"
              src={job.outputUrl}
              controls
              playsInline
            />
          )}
          <p className={HINT}>
            Saved to your Gallery
            {job.outputBytes
              ? ` (${(job.outputBytes / 1048576).toFixed(1)} MB)`
              : ""}
            . Use it in a broadcast, template, Status or live chat.
          </p>
        </>
      )}
      {failed && (
        <p className={ERROR_TEXT}>{job.error || "Something went wrong."}</p>
      )}
    </Modal>
  );
}

function localSize(
  aspect: AspectKey,
  w: number,
  h: number,
  quality: Quality,
): OutputSize {
  const L = quality === "HD" ? 1920 : 1280;
  const S = quality === "HD" ? 1080 : 720;
  if (aspect === "PORTRAIT_9_16") return { width: S, height: L };
  if (aspect === "SQUARE_1_1") return { width: S, height: S };
  if (aspect === "LANDSCAPE_16_9") return { width: L, height: S };
  const s = Math.min(1, L / Math.max(w || L, h || L));
  return {
    width: Math.round(((w || S) * s) / 2) * 2,
    height: Math.round(((h || L) * s) / 2) * 2,
  };
}
